import React from 'react';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';

import {
  fetchBudgetPeriods,
  fetchCostCenters,
  fetchRequisitionStates,
  fetchReviewQueue,
} from './forms.apis';
import { ILookupItem } from './forms.models';
import {
  IReviewQueueFilters,
  IReviewQueueItem,
} from './review.models';

type Props = {
  navigate?: (path: string) => void;
};

type State = {
  loading: boolean;
  error: string;
  rows: IReviewQueueItem[];
  total: number;

  filters: IReviewQueueFilters;
  periods: ILookupItem[];
  costcenters: ILookupItem[];
  states: ILookupItem[];
};

const DEFAULT_FILTERS: IReviewQueueFilters = {
  search: '',
  periodix: '',
  costcenterix: '',
  stateix: '',
  page: 1,
  pagesize: 20,
};

export default class ReviewQueuePage extends React.Component<any, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      error: '',
      rows: [],
      total: 0,

      filters: { ...DEFAULT_FILTERS },
      periods: [],
      costcenters: [],
      states: [],
    };
  }

  componentDidMount(): void {
    this.loadInitialData();
  }

  private loadInitialData = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [periods, costcenters, states] = await Promise.all([
        fetchBudgetPeriods(),
        fetchCostCenters(),
        fetchRequisitionStates(),
      ]);

      this.setState(
        {
          periods,
          costcenters,
          states,
        },
        () => this.loadRows()
      );
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load review queue lookups',
      });
    }
  };

  private loadRows = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const result = await fetchReviewQueue(this.state.filters);

      this.setState({
        rows: result.items || [],
        total: result.total || 0,
        loading: false,
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load review queue',
      });
    }
  };

  private handleTextChange =
    (field: keyof IReviewQueueFilters) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        filters: {
          ...prevState.filters,
          [field]: value,
          page: 1,
        },
      }));
    };

  private handleSelectChange =
    (field: keyof IReviewQueueFilters) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        filters: {
          ...prevState.filters,
          [field]: value,
          page: 1,
        },
      }));
    };

  private handleSearch = (): void => {
    this.loadRows();
  };

  private handleReset = (): void => {
    this.setState(
      {
        filters: { ...DEFAULT_FILTERS },
      },
      () => this.loadRows()
    );
  };

  private handlePaginationChange = (model: GridPaginationModel): void => {
    this.setState(
      (prevState) => ({
        filters: {
          ...prevState.filters,
          page: model.page + 1,
          pagesize: model.pageSize,
        },
      }),
      () => this.loadRows()
    );
  };

  // private handleOpen = (row: IReviewQueueItem): void => {
  //   const path = `/forms/requisitions/${row.recix}/review`;

  //   if (this.props.navigate) {
  //     this.props.navigate(path);
  //     return;
  //   }

  //   window.location.href = path;
  // };

  private handleOpen = (row: IReviewQueueItem): void => {
    if (this.props.navigate) {
      //this.props.navigate(path);
      this.props.navigate('process',row);
      return;
    }
  };

  private getColumns = (): GridColDef<IReviewQueueItem>[] => {
    return [
      {
        field: 'reference',
        headerName: 'Reference',
        minWidth: 150,
        flex: 0.8,
      },
      {
        field: 'recname',
        headerName: 'Title',
        minWidth: 240,
        flex: 1.5,
      },
      {
        field: 'costcenter',
        headerName: 'Cost Center',
        minWidth: 160,
        flex: 1,
        valueGetter: (_value, row) => row.costcenter?.recname || '',
      },
      {
        field: 'period',
        headerName: 'Budget Period',
        minWidth: 140,
        flex: 0.8,
        valueGetter: (_value, row) => row.period?.recname || '',
      },
      {
        field: 'requesteddate',
        headerName: 'Requested Date',
        minWidth: 130,
        flex: 0.8,
        valueGetter: (_value, row) =>
          row.requesteddate ? moment(row.requesteddate).format('YYYY-MM-DD') : '',
      },
      {
        field: 'totalestimate',
        headerName: 'Requested Total',
        minWidth: 140,
        flex: 0.8,
        type: 'number',
        valueFormatter: (value: any) => Number(value || 0).toLocaleString(),
      },
      {
        field: 'reviewedtotal',
        headerName: 'Reviewed Total',
        minWidth: 140,
        flex: 0.8,
        type: 'number',
        valueFormatter: (value: any) => Number(value || 0).toLocaleString(),
      },
      {
        field: 'reviewedcount',
        headerName: 'Reviewed',
        minWidth: 100,
        flex: 0.5,
        type: 'number',
      },
      {
        field: 'itemcount',
        headerName: 'Items',
        minWidth: 90,
        flex: 0.5,
        type: 'number',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 130,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<IReviewQueueItem>) => (
          <Button
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => this.handleOpen(params.row)}
          >
            Review
          </Button>
        ),
      },
    ];
  };

  render(): React.ReactNode {
    const { loading, error, rows, total, filters, periods, costcenters, states } = this.state;

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Review Queue
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Submitted requisitions awaiting finance review and classification.
            </Typography>
          </Box>
        </Stack>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.search}
                  onChange={this.handleTextChange('search')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2.5 }}>
                <FormControl fullWidth>
                  <InputLabel id="review-period-label">Budget Period</InputLabel>
                  <Select
                    labelId="review-period-label"
                    value={filters.periodix}
                    label="Budget Period"
                    onChange={this.handleSelectChange('periodix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {periods.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2.5 }}>
                <FormControl fullWidth>
                  <InputLabel id="review-cc-label">Cost Center</InputLabel>
                  <Select
                    labelId="review-cc-label"
                    value={filters.costcenterix}
                    label="Cost Center"
                    onChange={this.handleSelectChange('costcenterix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {costcenters.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="review-state-label">State</InputLabel>
                  <Select
                    labelId="review-state-label"
                    value={filters.stateix}
                    label="State"
                    onChange={this.handleSelectChange('stateix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {states.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 1 }}>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={this.handleSearch}>
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleReset}
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ height: 640, width: '100%' }}>
              {loading && rows.length === 0 ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: '100%' }}
                  spacing={2}
                >
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    Loading review queue...
                  </Typography>
                </Stack>
              ) : (
                <DataGrid
                  rows={rows}
                  columns={this.getColumns()}
                  getRowId={(row) => row.recix}
                  rowCount={total}
                  loading={loading}
                  paginationMode="server"
                  pageSizeOptions={[10, 20, 50, 100]}
                  paginationModel={{
                    page: filters.page - 1,
                    pageSize: filters.pagesize,
                  }}
                  onPaginationModelChange={this.handlePaginationChange}
                  disableRowSelectionOnClick
                  sx={{ border: 0 }}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }
}